import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { 
  CheckCircle, 
  ArrowLeft, 
  Shield, 
  CreditCard, 
  Smartphone, 
  QrCode,
  Package,
  Clock,
  User,
  Mail,
  Phone,
  Home,
  Lock,
  AlertCircle,
  Loader,
  MapPin,
  Truck,
  Calendar,
  Info
} from 'lucide-react'
import toast from 'react-hot-toast'
import cinetpayService from '../services/cinetpayService'
import firebaseService from '../services/firebaseService';

import '../styles/CinetPayPage.css'

export default function CinetPayPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const planType = queryParams.get('plan') || '10-stickers'
  
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [availablePacks, setAvailablePacks] = useState({})
  const [selectedPack, setSelectedPack] = useState(null)
  const [paymentMethods, setPaymentMethods] = useState([])
  const [selectedMethod, setSelectedMethod] = useState('mobile')
  const [mobileOperator, setMobileOperator] = useState('ORANGE')
  const [fieldErrors, setFieldErrors] = useState({})

  const [deliveryInfo, setDeliveryInfo] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: 'Abidjan',
    zone: '',
    notes: '',
    deliveryOption: 'standard'
  })
  const [deliveryCost, setDeliveryCost] = useState(0)

  useEffect(() => {
    // Charger les packs disponibles
    const packs = cinetpayService.getAvailablePacks()
    setAvailablePacks(packs)
    const defaultPack = packs[planType] || packs["10-stickers"]
    setSelectedPack(defaultPack)
    
    // Charger les méthodes de paiement
    const methods = cinetpayService.getAvailablePaymentMethods()
    setPaymentMethods(Object.entries(methods))
    
    // Calculer les frais de livraison
    updateDeliveryCost('Abidjan')
    
    // Récupérer les infos depuis sessionStorage
    const savedInfo = sessionStorage.getItem('ultimatag_delivery_info')
    if (savedInfo) {
      try {
        const parsedInfo = JSON.parse(savedInfo)
        setDeliveryInfo(prev => ({ ...prev, ...parsedInfo }))
        if (parsedInfo.city) {
          updateDeliveryCost(parsedInfo.city)
        }
      } catch (e) {
        console.error("Erreur parsing saved info:", e)
      }
    }
  }, [planType])

  const updateDeliveryCost = (city) => {
    const delivery = cinetpayService.calculateDelivery(city)
    setDeliveryCost(delivery.price)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    
    setDeliveryInfo(prev => {
      let newValue = value
      
      switch(name) {
        case 'email':
          // Email : pas d'espaces
          newValue = value.replace(/\s/g, '')
          break
          
        case 'phone':
          // Téléphone : que chiffres et espaces
          newValue = value.replace(/[^\d\s]/g, '')
          break
          
        case 'fullName':
          // Nom : lettres, espaces, tirets, apostrophes
          newValue = value.replace(/[^a-zA-Z\s\-']/g, '')
          break
          
        case 'address':
        case 'zone':
        case 'notes':
          // Adresse/Zone : on garde tout mais on évite les espaces multiples
          newValue = value.replace(/\s+/g, ' ')
          break
          
        default:
          newValue = value
      }
      
      const updated = { 
        ...prev, 
        [name]: newValue
      }
      
      if (name === 'city') {
        updateDeliveryCost(updated.city)
      }
      
      return updated
    })
  }
  
  // Supprimez la fonction cleanInput ou gardez-la pour d'autres usages
  const validateForm = () => {
    const errors = {}
    
    if (!deliveryInfo.fullName.trim()) {
      errors.fullName = 'Le nom complet est obligatoire'
    }
    
    if (!deliveryInfo.phone.trim()) {
      errors.phone = 'Le téléphone est obligatoire'
    } else {
      const cleanPhone = deliveryInfo.phone.trim().replace(/\s/g, '')
      if (!/^(\+225|0|225)[0-9]{8,10}$/.test(cleanPhone)) {
        errors.phone = 'Numéro invalide (format: 07 12 34 56 78)'
      }
    }
    
    if (deliveryInfo.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(deliveryInfo.email)) {
      errors.email = 'Email invalide'
    }
    
    if (!deliveryInfo.address.trim()) {
      errors.address = 'L\'adresse est obligatoire'
    }
    
    if (!deliveryInfo.zone.trim()) {
      errors.zone = 'Le quartier/zone est obligatoire'
    }
    
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }
  

  const handlePayment = async () => {
    if (!validateForm() || !selectedPack) {
      toast.error(!selectedPack ? 'Sélectionnez un pack' : 'Corrigez les erreurs');
      return;
    }
    
    setProcessing(true);
    setLoading(true);
  
    try {
      const totalAmount = selectedPack.price + deliveryCost;
      const cleanPhone = deliveryInfo.phone.replace(/\s/g, '');
      
      // 1. Initialiser le paiement CinetPay
      const paymentResult = await cinetpayService.initPayment({
        amount: totalAmount,
        packId: selectedPack.id,
        packName: selectedPack.name,
        quantity: selectedPack.quantity,
        customerName: deliveryInfo.fullName,
        customerEmail: deliveryInfo.email || 'client@ultimatag.com',
        customerPhone: cleanPhone,
        deliveryAddress: `${deliveryInfo.address}, ${deliveryInfo.zone}`,
        deliveryCity: deliveryInfo.city,
        deliveryCost: deliveryCost
      });
  
      if (!paymentResult.success) throw new Error(paymentResult.message);
  
      // 2. Sauvegarder dans Firebase avec TOUTES les informations
      const firebaseData = {
        // Transaction
        transactionId: paymentResult.transactionId,
        montant: totalAmount,
        
        // Client (champs plats)
        clientNom: deliveryInfo.fullName,
        clientEmail: deliveryInfo.email || '',
        clientTelephone: cleanPhone,
        
        // Adresse livraison (champs plats)
        adresseLivraison: deliveryInfo.address,
        villeLivraison: deliveryInfo.city,
        zoneLivraison: deliveryInfo.zone,
        instructionsLivraison: deliveryInfo.notes || '',
        fraisLivraison: deliveryCost,
        
        // Pack
        packNom: selectedPack.name,
        packId: selectedPack.id,
        quantite: selectedPack.quantity,
        prixUnitaire: selectedPack.price,
        
        // Paiement
        methodePaiement: selectedMethod,
        operateurPaiement: selectedMethod === 'mobile' ? mobileOperator : null,
        
        // Structures complètes
        client: {
          nomComplet: deliveryInfo.fullName,
          email: deliveryInfo.email || '',
          telephone: cleanPhone,
          adresse: deliveryInfo.address,
          ville: deliveryInfo.city,
          zone: deliveryInfo.zone
        },
        
        livraison: {
          adresse: deliveryInfo.address,
          ville: deliveryInfo.city,
          zone: deliveryInfo.zone,
          instructions: deliveryInfo.notes || '',
          frais: deliveryCost
        },
        
        pack: {
          id: selectedPack.id,
          nom: selectedPack.name,
          quantite: selectedPack.quantity,
          prixUnitaire: selectedPack.price
        },
        
        paiement: {
          methode: selectedMethod,
          operateur: selectedMethod === 'mobile' ? mobileOperator : null,
          statut: 'PENDING',
          transactionId: paymentResult.transactionId
        },
        
        totaux: {
          sousTotal: selectedPack.price,
          fraisLivraison: deliveryCost,
          total: totalAmount
        }
      };
  
      console.log('📤 Données Firebase complètes:', firebaseData);
  
firebaseService.createVente({
  transactionId: paymentResult.transactionId,
  montant: totalAmount,
  
  // Client
  clientNom: deliveryInfo.fullName,
  clientEmail: deliveryInfo.email || '',
  clientTelephone: cleanPhone,
  
  // Pack
  packNom: selectedPack.name,
  packId: selectedPack.id,
  quantite: selectedPack.quantity,
  
  // Livraison (TOUS LES CHAMPS)
  adresseLivraison: deliveryInfo.address,
  villeLivraison: deliveryInfo.city,
  zoneLivraison: deliveryInfo.zone,
  fraisLivraison: deliveryCost,
  instructionsLivraison: deliveryInfo.notes || '',
  
  // Paiement
  methodePaiement: selectedMethod,
  operateurPaiement: selectedMethod === 'mobile' ? mobileOperator : null,
  statutPaiement: 'PENDING'
});
      // 3. Sauvegarde locale
      sessionStorage.setItem('ultimatag_delivery_info', JSON.stringify(deliveryInfo));
      sessionStorage.setItem('current_transaction_id', paymentResult.transactionId);
      sessionStorage.setItem(`order_${paymentResult.transactionId}`, JSON.stringify({
        transactionId: paymentResult.transactionId,
        pack: selectedPack.name,
        amount: totalAmount,
        customer: { 
          name: deliveryInfo.fullName, 
          phone: cleanPhone,
          email: deliveryInfo.email,
          address: deliveryInfo.address,
          city: deliveryInfo.city,
          zone: deliveryInfo.zone
        },
        delivery: {
          cost: deliveryCost,
          notes: deliveryInfo.notes
        },
        payment: {
          method: selectedMethod,
          operator: selectedMethod === 'mobile' ? mobileOperator : null
        },
        status: 'PENDING',
        date: new Date().toISOString()
      }));
  
      // 4. Redirection
      toast.success('Redirection vers le paiement...');
      setTimeout(() => window.location.href = paymentResult.paymentUrl, 2000);
  
    } catch (error) {
      console.error('❌ Erreur:', error);
      toast.error(error.message || 'Erreur de paiement');
      setProcessing(false);
      setLoading(false);
    }
  };

// Fonction utilitaire pour les messages d'erreur
const getCinetPayErrorMessage = (code) => {
  const errors = {
    '608': 'Paramètre obligatoire manquant',
    '609': 'Clé API incorrecte',
    '613': 'Site ID incorrect',
    '624': 'Erreur de traitement',
    '429': 'Trop de requêtes',
    '403': 'Format de requête invalide'
  };
  return errors[code] || null;
};

  const cleanInput = (value) => {
    if (typeof value !== 'string') return ''
    return value.replace(/\s+/g, ' ').trim()
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CI', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(price)
  }

  const renderDeliveryEstimate = (city) => {
    const delivery = cinetpayService.calculateDelivery(city)
    return (
      <div className={`cinetpay-delivery-estimate ${delivery.free ? 'cinetpay-delivery-free' : 'cinetpay-delivery-paid'}`}>
        <Truck size={16} />
        <span>
          {delivery.free ? 'Livraison gratuite' : `Frais: ${formatPrice(delivery.price)}`}
          <span className="cinetpay-delivery-days"> • {delivery.days}</span>
        </span>
      </div>
    )
  }

  // Ajoutez cette fonction pour fermer l'overlay en cas d'erreur
  const closeProcessingOverlay = () => {
    setProcessing(false)
    setLoading(false)
  }

  return (
    <div className="cinetpay-page">
      <header className="cinetpay-header">
        <div className="cinetpay-container">
          <div className="cinetpay-header-content">
            <button 
              onClick={() => navigate('/')}
              className="cinetpay-back-button"
              disabled={processing}
            >
              <ArrowLeft size={20} />
              Retour aux packs
            </button>
            <div className="cinetpay-header-brand">
              <h1>UltimaTag</h1>
              <p className="cinetpay-header-subtitle">Commande de stickers QR</p>
            </div>
            <div className="cinetpay-header-security">
              <Shield size={20} />
              <span>Paiement 100% sécurisé</span>
            </div>
          </div>
        </div>
      </header>

      <main className="cinetpay-main">
        <div className="cinetpay-container">
          <div className="cinetpay-layout">
            {/* Formulaire de commande */}
            <div className="cinetpay-form-section">
              <div className="cinetpay-form-header">
                <h2 className="cinetpay-section-title">
                  <User size={24} />
                  Informations de livraison
                </h2>
                <p className="cinetpay-section-subtitle">
                  Remplissez vos coordonnées pour recevoir vos stickers
                </p>
              </div>

              <div className="cinetpay-form-container">
                {/* Informations personnelles */}
                <div className="cinetpay-form-section-inner">
                  <h3 className="cinetpay-form-section-title">
                    <User size={18} />
                    Coordonnées
                  </h3>
                  <div className="cinetpay-form-grid">
                    <div className="cinetpay-form-group">
                      <label className="cinetpay-form-label cinetpay-required">
                        <User size={16} />
                        Nom complet
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={deliveryInfo.fullName}
                        onChange={handleInputChange}
                        className="cinetpay-form-input"
                        placeholder="Votre nom complet"
                        required
                        disabled={processing}
                      />
                      {fieldErrors.fullName && (
                        <span className="cinetpay-error">{fieldErrors.fullName}</span>
                      )}
                    </div>

                    <div className="cinetpay-form-group">
                      <label className="cinetpay-form-label cinetpay-required">
                        <Phone size={16} />
                        Numéro de téléphone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={deliveryInfo.phone}
                        onChange={handleInputChange}
                        className="cinetpay-form-input"
                        placeholder="07 12 34 56 78"
                        required
                        disabled={processing}
                      />
                      <small className="cinetpay-form-help">
                        Format: 07 12 34 56 78 ou 225 XX XX XX XX
                      </small>
                      {fieldErrors.phone && (
                        <span className="cinetpay-error">{fieldErrors.phone}</span>
                      )}
                    </div>

                    <div className="cinetpay-form-group">
                      <label className="cinetpay-form-label">
                        <Mail size={16} />
                        Adresse email (optionnel)
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={deliveryInfo.email}
                        onChange={handleInputChange}
                        className="cinetpay-form-input"
                        placeholder="votre@email.com"
                        disabled={processing}
                      />
                      {fieldErrors.email && (
                        <span className="cinetpay-error">{fieldErrors.email}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Adresse de livraison */}
                <div className="cinetpay-form-section-inner">
                  <h3 className="cinetpay-form-section-title">
                    <MapPin size={18} />
                    Adresse de livraison
                  </h3>
                  <div className="cinetpay-form-grid">
                    <div className="cinetpay-form-group">
                      <label className="cinetpay-form-label cinetpay-required">
                        Ville
                      </label>
                      <select
                        name="city"
                        value={deliveryInfo.city}
                        onChange={handleInputChange}
                        className="cinetpay-form-select"
                        disabled={processing}
                      >
                        <option value="Abidjan">Abidjan</option>
                        <option value="Yamoussoukro">Yamoussoukro</option>
                        <option value="Bouaké">Bouaké</option>
                        <option value="Daloa">Daloa</option>
                        <option value="Korhogo">Korhogo</option>
                        <option value="Autre">Autre ville</option>
                      </select>
                      {renderDeliveryEstimate(deliveryInfo.city)}
                    </div>

                    <div className="cinetpay-form-group">
                      <label className="cinetpay-form-label cinetpay-required">
                        Quartier/Zone
                      </label>
                      <input
                        type="text"
                        name="zone"
                        value={deliveryInfo.zone}
                        onChange={handleInputChange}
                        className="cinetpay-form-input"
                        placeholder="Ex: Cocody, Marcory, Plateau..."
                        required
                        disabled={processing}
                      />
                      {fieldErrors.zone && (
                        <span className="cinetpay-error">{fieldErrors.zone}</span>
                      )}
                    </div>

                    <div className="cinetpay-form-group cinetpay-full-width">
                      <label className="cinetpay-form-label cinetpay-required">
                        Adresse détaillée
                      </label>
                      <textarea
                        name="address"
                        value={deliveryInfo.address}
                        onChange={handleInputChange}
                        className="cinetpay-form-textarea"
                        rows="3"
                        placeholder="Rue, avenue, immeuble, étage, repères..."
                        required
                        disabled={processing}
                      />
                      <small className="cinetpay-form-help">
                        Précisez au maximum pour une livraison facile
                      </small>
                      {fieldErrors.address && (
                        <span className="cinetpay-error">{fieldErrors.address}</span>
                      )}
                    </div>

                    <div className="cinetpay-form-group cinetpay-full-width">
                      <label className="cinetpay-form-label">
                        Instructions de livraison (optionnel)
                      </label>
                      <textarea
                        name="notes"
                        value={deliveryInfo.notes}
                        onChange={handleInputChange}
                        className="cinetpay-form-textarea"
                        rows="2"
                        placeholder="Ex: Livrer après 18h, Appeler avant de venir, Code de portail..."
                        disabled={processing}
                      />
                    </div>
                  </div>
                </div>

                {/* Méthode de paiement */}
                <div className="cinetpay-form-section-inner">
                  <h3 className="cinetpay-form-section-title">
                    <CreditCard size={18} />
                    Méthode de paiement
                  </h3>
                  
                  <div className="cinetpay-payment-methods">
                    {paymentMethods.map(([key, method]) => (
                      <button
                        key={key}
                        type="button"
                        className={`cinetpay-payment-method ${selectedMethod === key ? 'cinetpay-selected' : ''}`}
                        onClick={() => setSelectedMethod(key)}
                        disabled={processing}
                      >
                        {key === 'mobile' && <Smartphone size={24} />}
                        {key === 'card' && <CreditCard size={24} />}
                        {key === 'wallet' && <CreditCard size={24} />}
                        <div className="cinetpay-method-info">
                          <h4 className="cinetpay-method-name">{method.name}</h4>
                          <p className="cinetpay-method-description">{method.description}</p>
                        </div>
                        {selectedMethod === key && (
                          <CheckCircle size={20} className="cinetpay-method-check" />
                        )}
                      </button>
                    ))}
                  </div>

                 
                </div>

                {/* Bouton de commande */}
                <div className="cinetpay-action-section">
                  <button
                    onClick={handlePayment}
                    disabled={processing || loading || !deliveryInfo.fullName.trim() || !deliveryInfo.phone.trim() || !deliveryInfo.address.trim() || !deliveryInfo.zone.trim()}
                    className="cinetpay-pay-button"
                  >
                    {processing ? (
                      <>
                        <Loader size={20} className="cinetpay-spinner" />
                        Préparation de la commande...
                      </>
                    ) : loading ? (
                      <>
                        <Loader size={20} className="cinetpay-spinner" />
                        Initialisation du paiement...
                      </>
                    ) : (
                      <>
                        <Lock size={20} />
                        Commander et payer {formatPrice((selectedPack?.price || 0) + deliveryCost)}
                      </>
                    )}
                  </button>
                  
                  <div className="cinetpay-security-info">
                    <Shield size={16} />
                    <span>Paiement sécurisé par CinetPay</span>
                    <span className="cinetpay-secure-badge">SSL</span>
                  </div>
                  
                  <div className="cinetpay-note">
                    <Info size={16} />
                    <p>
                      Vous serez redirigé vers la page sécurisée de CinetPay.
                      Après paiement, vous serez automatiquement redirigé vers la page de confirmation.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Récapitulatif de commande */}
            <div className="cinetpay-summary-section">
              <div className="cinetpay-summary-card">
                <h2 className="cinetpay-section-title">
                  <Package size={24} />
                  Votre commande
                </h2>

                {/* Sélecteur de pack */}
                <div className="cinetpay-pack-selector">
                  <label className="cinetpay-form-label cinetpay-required">
                    Choisir votre pack
                  </label>
                  <div className="cinetpay-packs-grid">
                    {Object.entries(availablePacks).map(([key, pack]) => (
                      <button
                        key={key}
                        type="button"
                        className={`cinetpay-pack-option ${
                          selectedPack?.id === pack.id ? 'cinetpay-selected-pack' : ''
                        }`}
                        onClick={() => setSelectedPack(pack)}
                        disabled={processing}
                      >
                        <div className="cinetpay-pack-option-header">
                          <h4 className="cinetpay-pack-option-name">{pack.name}</h4>
                          <div className="cinetpay-pack-option-price">
                            {formatPrice(pack.price)}
                          </div>
                        </div>
                        <div className="cinetpay-pack-option-quantity">
                          <QrCode size={14} />
                          {pack.quantity} stickers
                        </div>
                        {selectedPack?.id === pack.id && (
                          <div className="cinetpay-pack-option-check">
                            <CheckCircle size={16} />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Pack sélectionné */}
                {selectedPack && (
                  <div className="cinetpay-selected-pack">
                    <div className="cinetpay-pack-header">
                      <h3 className="cinetpay-pack-name">{selectedPack.name}</h3>
                      <div className="cinetpay-pack-quantity">
                        <QrCode size={16} />
                        {selectedPack.quantity} stickers
                      </div>
                    </div>
                    
                    <div className="cinetpay-pack-description">
                      {selectedPack.description}
                    </div>
                    
                    <ul className="cinetpay-pack-features">
                      {selectedPack.features.map((feature, index) => (
                        <li key={index} className="cinetpay-feature-item">
                          <CheckCircle size={14} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="cinetpay-pack-delivery">
                      <Truck size={16} />
                      <span>Livraison: {selectedPack.delivery}</span>
                    </div>
                  </div>
                )}

                {/* Détails de prix */}
                <div className="cinetpay-price-breakdown">
                  <h4 className="cinetpay-price-title">Détails du prix</h4>
                  
                  <div className="cinetpay-price-row">
                    <span>Pack {selectedPack?.name}</span>
                    <span>{formatPrice(selectedPack?.price || 0)}</span>
                  </div>
                  
                  <div className="cinetpay-price-row">
                    <span>
                      Frais de livraison
                      {deliveryCost === 0 && <span className="cinetpay-free-tag">GRATUIT</span>}
                    </span>
                    <span className={deliveryCost === 0 ? 'cinetpay-free' : ''}>
                      {deliveryCost === 0 ? 'Gratuit' : formatPrice(deliveryCost)}
                    </span>
                  </div>
                  
                  <div className="cinetpay-price-row cinetpay-total">
                    <strong>Total à payer</strong>
                    <strong className="cinetpay-total-price">
                      {formatPrice((selectedPack?.price || 0) + deliveryCost)}
                    </strong>
                  </div>
                </div>

                {/* Informations produit */}
                <div className="cinetpay-product-info">
                  <h4 className="cinetpay-info-title">
                    <Info size={18} />
                    À propos de votre commande
                  </h4>
                  
                  <div className="cinetpay-info-items">
                    <div className="cinetpay-info-item">
                      <Package size={16} />
                      <div>
                        <strong>Stickers pré-imprimés</strong>
                        <p>QR codes uniques déjà générés et imprimés</p>
                      </div>
                    </div>
                    
                    <div className="cinetpay-info-item">
                      <Shield size={16} />
                      <div>
                        <strong>Prêt à l'emploi</strong>
                        <p>Collez directement sur vos objets</p>
                      </div>
                    </div>
                    
                    <div className="cinetpay-info-item">
                      <Calendar size={16} />
                      <div>
                        <strong>Délai de livraison</strong>
                        <p>Basé sur votre ville de livraison</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Garantie */}
                <div className="cinetpay-guarantee">
                  <Shield size={20} />
                  <div className="cinetpay-guarantee-content">
                    <strong>Satisfait ou remboursé</strong>
                    <p>14 jours pour retourner votre commande</p>
                  </div>
                </div>
              </div>

              {/* Support */}
              <div className="cinetpay-support-card">
                <h3 className="cinetpay-support-title">
                  <AlertCircle size={20} />
                  Questions sur votre commande ?
                </h3>
                <p className="cinetpay-support-text">
                  Notre équipe logistique est disponible pour vous aider
                </p>
                <div className="cinetpay-contact-info">
                  <Phone size={16} />
                  <span>+225 07 12 20 49 86</span>
                </div>
                <div className="cinetpay-contact-info">
                  <Mail size={16} />
                  <span>logistique@ultimatag.com</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Overlay de traitement */}
      {processing && (
        <div className="cinetpay-processing-overlay">
          <div className="cinetpay-processing-modal">
            <Loader size={48} className="cinetpay-processing-spinner" />
            <h3>Préparation de votre commande...</h3>
            <p>Redirection vers la page de paiement sécurisée.</p>
            <p className="cinetpay-processing-note">
              Veuillez patienter, vous allez être redirigé vers CinetPay.
            </p>
            <button 
              onClick={closeProcessingOverlay}
              className="cinetpay-cancel-button"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  )
}